<template>
    <v-dialog
        v-model="show"
        persistent
        eager
        :width="dialogWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card role="region" aria-label="add-tags-dialog">
            <v-card-title>
                <span>{{ dialogTitle }}</span>
                <help-button :href="helpUrl"></help-button>
                <span
                    v-show="formErrorMessage.length"
                    class="ml-5 text-body-1 error--text"
                    >{{ formErrorMessage }}</span
                >
            </v-card-title>
            <v-card-text class="pb-2">
                <v-form ref="form" v-model="isFormValid">
                    <v-container pa-0 fluid>
                        <v-row
                            v-for="(item, index) in items"
                            :key="`item-${index}`"
                            class="align-center"
                        >
                            <v-col class="py-0">
                                <v-text-field
                                    v-model="item.name"
                                    label="Tag name *"
                                    required
                                    dense
                                    maxlength="300"
                                    :rules="tagNameRules"
                                    @input="onFormChange"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="4" class="py-0">
                                <v-select
                                    v-model="item.type"
                                    label="Type"
                                    dense
                                    required
                                    :rules="typeRules"
                                    :items="tagTypes"
                                    @change="onFormChange"
                                ></v-select>
                            </v-col>
                            <v-col
                                v-show="!item.valid"
                                cols="auto"
                                class="py-0"
                            >
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon color="warning" v-on="on">{{
                                            icons.warning
                                        }}</v-icon>
                                    </template>
                                    <span>{{ item.message }}</span>
                                </v-tooltip>
                            </v-col>
                            <v-col cols="auto" class="py-0">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            v-show="
                                                index === items.length - 1 &&
                                                    allowMoreItems
                                            "
                                            small
                                            icon
                                            color="primary"
                                            aria-label="add-tag"
                                            v-on="on"
                                            @click="onAddItemClick"
                                        >
                                            <v-icon>{{ icons.add }}</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Add tag</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            v-show="
                                                index < items.length - 1 ||
                                                    !allowMoreItems
                                            "
                                            small
                                            icon
                                            color="primary"
                                            aria-label="remove-tag"
                                            v-on="on"
                                            @click="onRemoveItemClick(index)"
                                        >
                                            <v-icon>{{ icons.remove }}</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Remove tag</span>
                                </v-tooltip>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-card-actions class="px-4 justify-end">
                <v-btn outlined small @click="onCancelClick">Cancel</v-btn>
                <v-btn
                    color="primary"
                    class="ml-3"
                    small
                    :loading="isSaveLoading"
                    :disabled="!allowSave"
                    @click="onSaveClick"
                    >Save</v-btn
                >
            </v-card-actions>
        </v-card>
        <confirmation-dialog ref="confirmation"></confirmation-dialog>
    </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import HelpButton from '@/components/Buttons/HelpButton';

export default {
    name: 'TagDetailsDialog',
    components: {
        'confirmation-dialog': ConfirmationDialog,
        'help-button': HelpButton
    },
    props: {
        show: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            isFormValid: false,
            isFormDirty: false,
            isFormEditing: false,
            // local form fields. to be copied form vuex object
            maxAllowedItems: 5,
            items: [{ name: '', type: 'Device', valid: true, message: '' }],
            tagTypes: ['Device', 'Space'],
            formErrorMessage: '',
            // form validation rules
            tagNameRules: [
                v => !!v || 'Tag name is required',
                v =>
                    (v && v.length <= 300) ||
                    'Tag name must be less than 300 characters'
            ],
            typeRules: [v => !!v || 'Type is required'],
            // the buttons loading variables
            isSaveLoading: false
        };
    },
    computed: {
        ...mapState('tag', {
            selectedTag: 'tag',
            helpUrl: 'helpUrl'
        }),
        allowSave() {
            return this.isFormDirty && this.isFormValid && !this.isSaveLoading;
        },
        dialogTitle() {
            return 'Add tags';
        },
        dialogWidth() {
            if (this.$vuetify.breakpoint.sm) {
                return '95%';
            } else {
                return '400';
            }
        },
        allowMoreItems() {
            return this.items.length < this.maxAllowedItems;
        }
    },
    methods: {
        ...mapActions('tag', ['createTags', 'checkIsTagUnique']),
        // ========================== Events ==============================
        onFormChange() {
            this.isFormDirty = true;
        },
        onCancelClick() {
            if (this.isFormDirty) {
                this.$refs.confirmation
                    .open(
                        'Add Tags',
                        'Are you sure, you want to close without saving?',
                        'Form data has been modified',
                        {
                            confirmActionText: 'Let me save',
                            cancelActionText: 'Close without saving',
                            showIcon: true
                        }
                    )
                    .then(res => {
                        if (!res) {
                            this.closeThisDialog();
                        }
                    });
            } else {
                this.closeThisDialog();
            }
        },
        onSaveClick() {
            this.saveThisTag();
        },
        onAddItemClick() {
            if (this.allowMoreItems) {
                this.items.push({
                    name: '',
                    type: 'Device',
                    valid: true,
                    message: ''
                });
            }
        },
        onRemoveItemClick(index) {
            if (index >= 0 && this.items && this.items.length > index) {
                this.items.splice(index, 1);
            }
        },
        onEscapeKeydown() {
            this.onCancelClick();
        },
        // ========================== Others =============================
        async closeThisDialog(refreshParentData = false, timeout = 500) {
            setTimeout(() => {
                this.resetDialogData();
                this.$emit('close', refreshParentData);
            }, timeout);
        },
        resetDialogData() {
            this.$refs.form.resetValidation();
            this.items = [];
            this.isFormDirty = false;
            this.isFormEditing = false;
            this.formErrorMessage = '';
            this.isSaveLoading = false;
        },
        async saveThisTag() {
            try {
                this.isSaveLoading = true;
                if (this.isFormDirty) {
                    // need to call the api to create new space
                    const isValid = await this.isFormDataValid();
                    if (!isValid) {
                        return;
                    }
                    const tags = this.items.map(item => ({
                        name: item.name,
                        type: item.type.toUpperCase()
                    }));
                    await this.createTags({ tags });
                    this.closeThisDialog(true, 1000);
                }
            } finally {
                this.isSaveLoading = false;
            }
        },
        async isFormDataValid() {
            const isValid = this.$refs.form.validate();
            let unique = true;
            this.formErrorMessage = '';
            // check for unique values within local array items
            this.items.forEach(tag => {
                if (
                    this.items.filter(
                        item =>
                            item.name.toLowerCase() ===
                                tag.name.toLowerCase() && item.type === tag.type
                    ).length > 1
                ) {
                    tag.valid = false;
                    tag.message = 'Duplicate tag name';
                    unique = false;
                } else {
                    tag.valid = true;
                    tag.message = '';
                }
            });
            if (!unique) {
                this.formErrorMessage = 'Tags below contains duplicate names!';
                return false;
            }

            // check for unique tags in the database
            for (const item of this.items) {
                const res = await this.checkIsTagUnique({
                    name: item.name,
                    type: item.type.toUpperCase()
                });
                if (!res) {
                    item.valid = false;
                    item.message = 'Tag name already added';
                    unique = false;
                } else {
                    item.valid = true;
                    item.message = '';
                }
            }
            if (!unique) {
                this.formErrorMessage = 'Some tags exists already!';
            }
            if (!isValid || !unique) {
                return false;
            } else {
                this.formErrorMessage = '';
                return true;
            }
        }
    }
};
</script>

<style lang="scss" scoped></style>
